import { cn, getAvatarName } from '@commenda/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { Tooltip } from '../Tooltip';
import { resolveImageSrc } from './resolveImageSrc';

export const avatarVariants = cva(
  // Circular by design: every size is a full circle (Figma "Avatars"). `overflow-hidden`
  // clips `image` to the circle; the badge lives OUTSIDE this element (see the wrapper
  // below) precisely because it must not be clipped. The hairline ring is the design's
  // "contrast border" — what separates one avatar from the next when a group overlaps them.
  'inline-flex items-center justify-center shrink-0 rounded-full bg-bg-secondary overflow-hidden text-fg-quaternary border-1/2 border-border-secondary',
  {
    variants: {
      size: {
        // 16 and 24 carry the type scale the design specifies for initials; the larger sizes
        // keep the text tokens, which already read correctly at those diameters.
        '2xs': 'size-4 text-[8px] font-medium leading-normal',
        xs: 'size-6 text-[10px] font-medium leading-normal',
        sm: 'size-8 txt-small-medium',
        md: 'size-10 txt-medium-medium',
        lg: 'size-12 txt-large-medium',
        xl: 'size-14 txt-extra-large-medium',
        '2xl': 'size-16 display-xs-medium',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

type AvatarVariants = VariantProps<typeof avatarVariants>;

// Presence/status dot, pinned to the avatar's bottom-right. Mirrors avatarVariants'
// size keys so the dot scales with the avatar (~1/3 of its diameter, matching Figma's
// 8px dot on a 24px avatar). The white border is a separating ring against the avatar
// fill; it grows from 1px → 2px so it stays visible as the dot grows. Default fill is
// utility-rosé-300 (the design's color); callers override via `badge.color`.
export const avatarBadgeVariants = cva(
  'absolute bottom-0 right-0 rounded-full bg-utility-rose-300 border-bg-primary',
  {
    variants: {
      size: {
        '2xs': 'size-1.5 border',
        xs: 'size-2 border',
        sm: 'size-2.5 border',
        md: 'size-3 border-2',
        lg: 'size-3.5 border-2',
        xl: 'size-4 border-2',
        '2xl': 'size-5 border-2',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

// Seeded avatar background. OKLCH (CSS Color 4) over HSL because its L axis is
// perceptually uniform — every hue at a given L reads as the same brightness,
// so lightness varies evenly across hues. The range is held dark enough that
// the (always-white) initials stay legible on every fill. Applied inline
// because arbitrary oklch() values aren't Tailwind utilities.
const AVATAR_BG_CHROMA = 0.14; // moderate: keeps every hue inside sRGB gamut
const AVATAR_BG_LIGHTNESS_MIN = 0.45;
const AVATAR_BG_LIGHTNESS_MAX = 0.62;

const hashSeed = (seed: string | number) => {
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (Math.imul(hash, 31) + str.charCodeAt(i)) | 0;
  }
  // Avalanche (MurmurHash3 fmix) so low-entropy seeds — e.g. autoincrement ids — don't map to adjacent colors.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x45d9f3b);
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x45d9f3b);
  hash ^= hash >>> 16;
  return hash >>> 0;
};

const seededAvatarBackground = (seed: string | number) => {
  const hash = hashSeed(seed);
  const hue = hash % 360;
  // Reuse the quotient (independent of hue's remainder) to sample L in-range.
  const lightFraction = Math.floor((hash / 360) % 256) / 256;
  const lightness =
    AVATAR_BG_LIGHTNESS_MIN +
    lightFraction * (AVATAR_BG_LIGHTNESS_MAX - AVATAR_BG_LIGHTNESS_MIN);
  return `oklch(${lightness} ${AVATAR_BG_CHROMA} ${hue})`;
};

/** Object form of the `badge` prop — overrides for the presence dot. */
type AvatarBadgeConfig = {
  color?: string;
  'aria-label'?: string;
};

type BaseAvatarProps = {
  size?: AvatarVariants['size'];
  className?: string;
  /** Deterministic background hashed from a stable id: same seed → same color,
   * always with white initials. Omit for the default neutral avatar. */
  colorSeed?: string | number;
  /** Initials shown in the fallback acronym. 1 (default) → first initial;
   * 2 → two-letter acronym (e.g. "John Doe" → "JD"). Opt in with 2. */
  initialsCount?: 1 | 2;
  /** Presence/status dot at the bottom-right corner (Figma Badge=True). `true`
   * shows the default rosé dot; pass an object to set its `color` (any CSS color)
   * or an accessible `aria-label`. A white ring always separates it from the avatar. */
  badge?: boolean | AvatarBadgeConfig;
  tooltip?: {
    content: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
    showArrow?: boolean;
  };
};

type AvatarProps = BaseAvatarProps & {
  /** Source of the fallback acronym. Omit (or pass empty) with no `image` to render
   * the neutral **placeholder** avatar (Figma Type=Placeholder). */
  name?: string;
  image?: string | null;
  altText?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;

const AvatarInner = ({
  name,
  image,
  altText,
  initialsCount,
}: Pick<AvatarProps, 'name' | 'image' | 'altText' | 'initialsCount'>) => {
  // Track image load failures (network errors, corrupt data, etc.) so we fall back
  // to initials instead of showing a broken-image icon.
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [image]);

  const resolvedSrc = resolveImageSrc(image);

  if (!resolvedSrc || failed) {
    return <span>{getAvatarName(name ?? '', initialsCount)}</span>;
  }

  return (
    <img
      src={resolvedSrc}
      alt={altText ?? name}
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      size,
      className,
      tooltip,
      name,
      image,
      altText,
      colorSeed,
      initialsCount = 1,
      badge,
      style,
      ...rest
    },
    ref
  ) => {
    const seededBackground =
      colorSeed != null ? seededAvatarBackground(colorSeed) : null;

    // Placeholder (Figma Type=Placeholder): no image, no initials, no seeded fill.
    // It swaps the neutral bg-bg-secondary for the design's darker bg-bg-tertiary.
    const isPlaceholder = !image && !name?.trim() && colorSeed == null;

    // Figma Type=Initials is an indigo circle with white initials. A colorSeed replaces the hue
    // per person (below) and keeps the white; without one, the design's indigo is the default.
    const isUnseededInitials = !image && !!name?.trim() && colorSeed == null;

    // `badge === true` → default dot; an object carries overrides; anything falsy → none.
    const badgeConfig: AvatarBadgeConfig | null =
      badge === true ? {} : badge || null;
    // Seeded fill overrides the neutral class background and pairs with white initials. The
    // contrast ring stays: it is what keeps overlapping avatars readable as separate circles.
    // Spread caller style first so ours wins on conflict.
    const mergedStyle = seededBackground
      ? { ...style, backgroundColor: seededBackground, color: '#ffffff' }
      : style;

    const avatarDiv = (
      <div
        ref={ref}
        className={cn(
          avatarVariants({ size }),
          isPlaceholder && 'bg-bg-tertiary',
          isUnseededInitials && 'bg-utility-indigo-500 text-fg-white',
          className
        )}
        style={mergedStyle}
        {...rest}
      >
        <AvatarInner
          name={name}
          image={image}
          altText={altText}
          initialsCount={initialsCount}
        />
      </div>
    );

    // The badge can't live inside avatarDiv (overflow-hidden would clip it), so when
    // present we wrap the avatar + dot in a relative box. The wrapper is sizing-neutral
    // (inline-flex, shrink-0) so it occupies exactly the avatar's footprint; ref still
    // lands on avatarDiv, never the wrapper.
    const avatarNode = badgeConfig ? (
      <div className="relative inline-flex shrink-0">
        {avatarDiv}
        <span
          role="status"
          aria-label={badgeConfig['aria-label']}
          className={avatarBadgeVariants({ size })}
          style={
            badgeConfig.color
              ? { backgroundColor: badgeConfig.color }
              : undefined
          }
        />
      </div>
    ) : (
      avatarDiv
    );

    return tooltip ? (
      <Tooltip
        content={tooltip.content}
        side={tooltip.side}
        showArrow={tooltip.showArrow}
        asChild
      >
        {avatarNode}
      </Tooltip>
    ) : (
      avatarNode
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar };
export type { AvatarBadgeConfig, AvatarProps };
